import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import EmptyState from '@app/src/components/EmptyState'
import Table from '@app/src/components/Table'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { getInquiryExportColumnsAccessor } from '@app/src/export-columns/inquiry'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useSelectableRowsWithPostSelectAll } from '@app/src/hooks/useSelectableRows/useSelectableRowsWithPostSelectAll'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import AccessorInquiryHeader from '@app/src/pages/ResourceCollection/Collections/Request/AccessorInquiryHeader'
import RequestsFilters from '@app/src/pages/ResourceCollection/Filters/RequestsFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { GenericOrganization } from '@app/src/types/organizations'
import type { Inquiry } from '@app/src/types/resourceExplorer'
import { InquiryStatus } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { br } from '@app/src/utils/translationMarkup'
import { ADMIN_ROLES, NotificationSeverity, ResourceTypes, WfRequestTags } from '@app/src/wf-constants'
import {
  ArrowForwardOutlined,
  DeleteOutlineOutlined,
  ListAltOutlined,
  NotificationsActiveOutlined,
} from '@mui/icons-material'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Button } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useConfirm } from 'material-ui-confirm'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { AccessorInquiryRow } from '../Request/AccessorInquiryRow'
import { ViewTypeName } from './ManageRequestsScene'
import RequestsReminderModal, { REQUEST_TYPES } from './RequestsReminder/RequestsReminderModal'

interface AccessorRequestsSceneProps {
  relatedContentFilter?: FilterGroup[]
  embedded?: boolean
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
  setCreateDialogOpen?: () => void
}

const allowedFilters = [
  'template.id',
  'status',
  'periodType',
  'provider.websiteAddress',
  'provider.countryId',
  'provider.vatNumber',
  'provider.id',
  'request.responseExternalStatus',
  'request.responseInternalStatus',
  'isActivated',
  'deadline',
  'createdAt',
  'activatedAt',
  'periodName',
  'provider.providerApprovalStatus',
  'provider.priority',
  'provider.finalRiskRating',
  'provider.activityStatus',
  'provider.providerOwners.userId',
  'provider.providerOwners.user',
  'provider.supplierUsage',
  'provider.tier',
  'provider.categoryOptions.id',
  'template.tags.tag',
  'provider.spends.spendClassification',
  'provider.spends.periodName',
  'provider.assessments.totalLevel',
  'provider.assessments.section1Level',
  'provider.assessments.section2Level',
  'provider.assessments.section3Level',
  'provider.assessments.section4Level',
  'provider.assessments.section5Level',
  'provider.assessments.assessmentTemplate.name',
  'provider.assessments.assessmentTemplate.id',
  'request.subscriptions.assessmentName',
  'request.subscriptions.totalLevel',
  'request.subscriptions.section1Level',
  'request.subscriptions.section2Level',
  'request.subscriptions.section3Level',
  'request.subscriptions.section4Level',
  'request.subscriptions.section5Level',
  'targetType',
  'targetProductId',
  'provider.mappingNodes.actorType.id',
  'provider.mappingNodes.tier',
  'provider.mappingNodes.product.productStatus',
]

export interface GroupedInquiries {
  responder: GenericOrganization
  requests: Inquiry[]
}

function groupInquiriesByResponder(requests: Inquiry[]): GroupedInquiries[] {
  const grouped = Object.groupBy(requests, request => request.provider.organizationId)

  return Object.entries(grouped).map(([organizationId, groupedRequests]) => ({
    responder: groupedRequests?.[0].provider.organization as GenericOrganization,
    requests: groupedRequests || [],
  }))
}

const AccessorRequestsScene: React.FC<AccessorRequestsSceneProps> = ({
  embedded,
  tabs,
  actionButtons = [],
  relatedContentFilter = [],
  setCreateDialogOpen,
}) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const apiFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [apiFilters])
  const { openDrawer } = useDrawer()
  const [isRequestsReminderDialogOpen, openRequestsReminderDialog, closeRequestsReminderDialog] = useDialogState(false)

  const confirm = useConfirm()
  const { mutateAsync: deleteInquiries, isPending: isDeletingInquiries } = useDeleteResource()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const { role, solution } = useAuthentication().scope
  const { hasPermission } = usePermissions()
  const isAdmin = ADMIN_ROLES.includes(role?.toLowerCase() ?? '')
  const isWorldfavorEmailUser = hasPermission(Permissions.WF_EMAIL_ACCESS)
  const { productMapping, showAdditionalExportColumns } = useFlags()
  const hasWriteAccess = useWriteAccess()

  const basePayload = {
    filter: relatedContentFilter,
    sort: sorting,
    include: [
      'creatorOrganization',
      'creatorOrganization.country',
      'creatorOrganization.industryNaceCode',
      'creatorOrganization.image',
      'creatorOrganization.locations',
      'request.responses',
      'request.responses.verifications',
      'template',
      'template.image',
      'provider.organization.contacts.user',
      'provider.organization.country',
      'provider.organization.industryNaceCode',
      'provider.organization.subIndustryNaceCode',
      'provider.providerOwners.user',
      'provider.organization.image',
      'provider.organization.locations',
      'provider.country.name',
      'requestAutomation',
      ...(productMapping ? ['targetProduct'] : []),
    ],
  }

  if (apiFilters.some(filter => filter.name.endsWith('Level'))) {
    const baselineAssessmentFilter: FilterGroup = {
      name: 'request.template.tags.tag',
      filters: [
        {
          value: WfRequestTags.PublicRequest3,
          operator: Operators.EqualTo,
        },
      ],
    }
    basePayload.filter.push(baselineAssessmentFilter)
  }

  const payload = {
    ...basePayload,
    filter: [...basePayload.filter, ...apiFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const {
    items: pageInquiries,
    count,
    isLoading,
    isFetching,
    isError,
  } = useFetchCollectionWithPost<Inquiry>({
    key: FetchKey.Request,
    endpoint: endpoints.inquiriesCollection,
    payload,
  })

  const showTargetProductColumn = useMemo(
    () => productMapping && pageInquiries.some(inquiry => inquiry.targetProduct),
    [pageInquiries, productMapping],
  )

  const {
    allRows,
    selectedRowsIds,
    setSelectedRowsIds,
    isLoadingAllRows,
    isAllSelected,
    handleSelectAll,
    handleCheckboxChange,
    isHeaderChecked,
  } = useSelectableRowsWithPostSelectAll<Inquiry>({
    rowsInPage: pageInquiries,
    basePayload: payload,
    fetchKey: FetchKey.AllInquiries,
    endpoint: endpoints.inquiriesCollection,
    count,
  })

  const inquiries = allRows.length ? allRows : pageInquiries

  const unhandledRequests = useMemo(
    () =>
      inquiries
        .filter(inquiry => selectedRowsIds.includes(inquiry.id))
        .filter(inquiry => inquiry.status !== InquiryStatus.Submitted && inquiry.status !== InquiryStatus.Approved)
        .filter(inquiry => !inquiry.provider.deletedAt),
    [selectedRowsIds, inquiries],
  )

  const unhandledRequestsByResponder = groupInquiriesByResponder(unhandledRequests)

  const handleCloseRequestReminder = () => {
    setSelectedRowsIds([])
    closeRequestsReminderDialog()
  }

  const onDeleted = {
    onSuccess: () => {
      showSnackbar({
        message: formatMessage({ id: 'notifications.successfulResourceDelete' }),
        severity: NotificationSeverity.success,
      })
      setSelectedRowsIds([])
      queryClient.invalidateQueries({ queryKey: [FetchKey.Inquiry] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Response] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Request] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Answer] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseItemCount] })
      queryClient.invalidateQueries({ queryKey: [FetchKey.Notification] })
    },
  }

  const confirmDeleteSelected = () => {
    confirm({
      description: formatMessage({ id: 'resourceCollections.delete.requests.dialogDescription' }),
      buttonOrder: ['confirm', 'cancel'],
      confirmationText: (
        <>
          <DeleteOutlineOutlined />
          {formatMessage({ id: 'general.delete' })}
        </>
      ),
      confirmationButtonProps: { style: { backgroundColor: 'white', color: 'black' } },
      cancellationButtonProps: { style: { backgroundColor: 'black', color: 'white' } },
    }).then(async () => {
      await deleteInquiries({ url: endpoints.deleteInquiriesByIds(selectedRowsIds) }, onDeleted)
    })
  }

  const confirmDeleteSelectedCompletely = () => {
    confirm({
      description: formatMessage(
        { id: 'resourceCollections.delete.requests.deleteCompletelyDescription' },
        { count: selectedRowsIds.length },
      ),
      buttonOrder: ['confirm', 'cancel'],
      confirmationText: (
        <>
          <DeleteOutlineOutlined />
          {formatMessage({ id: 'general.delete' })}
        </>
      ),
      confirmationButtonProps: { color: 'error' },
      cancellationButtonProps: { style: { backgroundColor: 'black', color: 'white' } },
    }).then(async () => {
      await deleteInquiries({ url: endpoints.deleteInquiriesByIds(selectedRowsIds, true) }, onDeleted)
    })
  }

  const handleExport = (selectedIds?: number[]) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.Inquiry}
        resourceView={tabs?.activeTabParam}
        count={count}
        userFilter={apiFilters}
        exportColumns={getInquiryExportColumnsAccessor(solution, productMapping, showAdditionalExportColumns)}
        rawExportPayload={{
          ...basePayload,
          include: [...basePayload.include, 'target', 'request.responses.history.creatorUser'],
        }}
        selectedIds={selectedIds}
      />,
    )
  }

  return (
    <>
      <ResourceCollectionScene
        title={formatMessage({ id: 'navbar.manageRequests' })}
        tabs={tabs}
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () => handleExport(selectedRowsIds),
            disabled: tabs?.activeTabParam === ViewTypeName.Overview || isLoading || isDeletingInquiries || count === 0,
          },
          ...actionButtons,
        ]}
        filter={
          <RequestsFilters
            implicitFilters={relatedContentFilter}
            allowedFilters={allowedFilters}
            resetPage={resetPage}
          />
        }
        embedded={embedded}
        buttonRow={
          selectedRowsIds.length
            ? [
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: 'resourceCollections.general.sendRequestReminder' },
                    { count: unhandledRequests.length },
                  ),
                  startIcon: <NotificationsActiveOutlined />,
                  onClick: openRequestsReminderDialog,

                  disabled:
                    !unhandledRequests.length || isLoading || isFetching || isLoadingAllRows || isDeletingInquiries,
                }),
                ...insertIf<ActionButton>(hasWriteAccess && isAdmin, {
                  label: formatMessage(
                    { id: 'resourceCollections.delete.requests.deleteCount' },
                    { count: selectedRowsIds?.length ?? 0 },
                  ),
                  startIcon: <DeleteOutlineOutlined />,
                  onClick: confirmDeleteSelected,
                  disabled: isLoading || isFetching || isLoadingAllRows || isDeletingInquiries,
                }),
                ...insertIf<ActionButton>(hasWriteAccess && isAdmin && isWorldfavorEmailUser, {
                  label: formatMessage(
                    { id: 'resourceCollections.delete.requests.deleteCompletelyCount' },
                    { count: selectedRowsIds?.length ?? 0 },
                  ),
                  startIcon: <DeleteForeverIcon color="error" />,
                  onClick: confirmDeleteSelectedCompletely,
                  color: 'error',
                  disabled: isLoading || isFetching || isLoadingAllRows || isDeletingInquiries,
                } as ActionButton),
                {
                  label: formatMessage(
                    { id: 'resourceCollections.general.exportWithCount' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: () => handleExport(selectedRowsIds),
                },
                ...insertIf<ActionButton>(count > pageSize, {
                  label: formatMessage({ id: 'resourceCollections.general.selectAllRequests' }, { count }),
                  variant: 'text',
                  onClick: handleSelectAll,

                  disabled: isLoading || isFetching || isAllSelected() || isDeletingInquiries,
                  loading: isLoadingAllRows,
                }),
              ]
            : undefined
        }
      >
        <Table<Inquiry>
          RowComponent={AccessorInquiryRow}
          rowComponentProps={{
            onCheckboxChange: handleCheckboxChange,
            selectedInquiriesIds: selectedRowsIds,
            showTargetProductColumn: showTargetProductColumn,
          }}
          HeaderComponent={() => (
            <AccessorInquiryHeader
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              isHeaderChecked={isHeaderChecked}
              inquiriesInPage={pageInquiries}
              setSelectedInquiriesIds={setSelectedRowsIds}
              showTargetProductColumn={showTargetProductColumn}
            />
          )}
          data={pageInquiries}
          isLoading={isLoading || isFetching || isDeletingInquiries}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          emptyState={
            <EmptyState
              iconComponent={ListAltOutlined}
              title={formatMessage({ id: 'table.manageRequests.requestsEmptyState.title' })}
              description={formatMessage({ id: 'table.manageRequests.requestsEmptyState.description' }, { br })}
              sx={{ my: 2 }}
            >
              {setCreateDialogOpen && hasWriteAccess && (
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardOutlined fontSize="small" />}
                  onClick={setCreateDialogOpen}
                >
                  {formatMessage({ id: 'table.manageRequests.requestsEmptyState.action' })}
                </Button>
              )}
            </EmptyState>
          }
          stickyColumnIndex={2}
        />
      </ResourceCollectionScene>
      <CreationModalProgressContextProvider>
        <RequestsReminderModal
          onClose={handleCloseRequestReminder}
          open={isRequestsReminderDialogOpen}
          unhandledRequests={unhandledRequests}
          unhandledRequestsByResponder={unhandledRequestsByResponder}
          requestType={REQUEST_TYPES.Questionnaire}
        />
      </CreationModalProgressContextProvider>
    </>
  )
}

export default AccessorRequestsScene
